import Request from '../../utilities/Request';
import { activeBudgetId, loadBatchDetail } from './import.js';

/**
 * The import rules page: every category rule of the budget's Monarch import, in the order they are
 * tried, read and written through Import_Domain's batch-free endpoints. Each write answers the same
 * listing as `import/rules`, so the page takes its list from whatever it last heard.
 */

export const IMPORT_RULES_LOAD_BEGIN = 'IMPORT_RULES_LOAD_BEGIN';
export const IMPORT_RULES_LOADED = 'IMPORT_RULES_LOADED';
export const IMPORT_RULES_LOAD_FAIL = 'IMPORT_RULES_LOAD_FAIL';
export const IMPORT_RULES_WRITE_BEGIN = 'IMPORT_RULES_WRITE_BEGIN';
export const IMPORT_RULES_WRITE_FAIL = 'IMPORT_RULES_WRITE_FAIL';
export const IMPORT_RULE_MATCHES_LOADED = 'IMPORT_RULE_MATCHES_LOADED';
export const IMPORT_RULES_ERROR_DISMISSED = 'IMPORT_RULES_ERROR_DISMISSED';

/** Numbers each listing asked for, so the reducer can tell the newest from one it overtook. */
let lastLoadRequest = 0;

/**
 * Lists the rules, and then counts what each matches among the staged rows. `counts: false` skips the
 * counting, for a reader that only shows the rules (the account summary), unless counts are already
 * on show: a new listing clears them, and the rules page showing them would be left without.
 *
 * Two listings can be in flight at once (the rules page's and the account summary's), and a write
 * answers with a listing of its own. An answer overtaken by a newer load or by a write is dropped
 * (the reducer's `loadRequest`), so a slow one never puts an older list back.
 */
export const loadImportRules = ({ counts = true } = {}) => (dispatch, getState) => {
	const budgetId = activeBudgetId(getState);
	const request = ++lastLoadRequest;
	dispatch({ type: IMPORT_RULES_LOAD_BEGIN, request });
	return Request.apiv1Get('import/rules')
		.then(whileBudget(getState, budgetId, (response) => {
			const before = rulesState(getState);
			if(before && before.loadRequest !== request) {
				return undefined;
			}
			const countsShown = !!(before && before.matches);
			dispatch({ type: IMPORT_RULES_LOADED, payload: response, request });
			return counts || countsShown ? dispatch(loadImportRuleMatches()) : undefined;
		}))
		.catch(whileBudget(getState, budgetId, (error) => {
			dispatch({ type: IMPORT_RULES_LOAD_FAIL, payload: errorMessage(error), request });
		}));
};

function rulesState(getState) {
	const state = typeof getState === 'function' ? getState() : null;
	return state && state.importRules ? state.importRules : null;
}

/**
 * What a draft rule would match among the rows imports have fetched, and the values each condition
 * could take beside the others (Import_Domain::rule_preview). `params` is previewParams()'s. Not kept
 * in the store: it belongs to the editor asking, and lasts only while that draft does.
 */
export const fetchImportRulePreview = (params) => Request.apiv1Get('import/rule_preview', params);

/**
 * How many staged rows each rule matches and decides. Only a hint, so a failure says nothing.
 *
 * The answer carries the listing it was asked against (`listingVersion`), and the reducer drops one
 * that a newer listing has overtaken: counts made before a move would show against the wrong rules.
 */
export const loadImportRuleMatches = () => (dispatch, getState) => {
	const budgetId = activeBudgetId(getState);
	const state = typeof getState === 'function' ? getState() : null;
	const listingVersion = state && state.importRules ? state.importRules.listingVersion : undefined;
	return Request.apiv1Get('import/rule_matches')
		.then(whileBudget(getState, budgetId, (response) => {
			dispatch({ type: IMPORT_RULE_MATCHES_LOADED, payload: response, listingVersion });
		}))
		.catch(() => {});
};

/**
 * Creates a rule (no `id`) or rewrites one. `rule` is the editor's draft, already in the endpoint's
 * terms: `amount` in cents or null, `position` a number or null for "wherever it belongs".
 *
 * Resolves `{ saved, error }`, so a refusal (a duplicate, a missing category) stays with the editor
 * that made it rather than becoming the page's.
 */
export const saveImportRule = (rule) => (dispatch, getState) => {
	const action = rule.action || 'assign';
	return ruleWrite(dispatch, getState, 'import/save_rule', {
		id: rule.id || null,
		external_account: rule.external_account || '',
		external_category: rule.no_category ? '' : (rule.external_category || ''),
		no_category: !!rule.no_category,
		external_merchant: rule.external_merchant || '',
		statement_contains: rule.statement_contains || '',
		amount: rule.amount === undefined ? null : rule.amount,
		action,
		category_id: action === 'assign' ? rule.category_id : null,
		reverse_sign: !!rule.reverse_sign,
		position: rule.position || null,
	});
};

/** Moves a rule to a 1-based position; the server clamps it and closes up the rest. */
export const moveImportRule = (id, position) => (dispatch, getState) => {
	return ruleWrite(dispatch, getState, 'import/move_rule', { id, position });
};

export const deleteImportRule = (id) => (dispatch, getState) => {
	return ruleWrite(dispatch, getState, 'import/delete_rule', { id });
};

export const dismissImportRulesError = () => ({ type: IMPORT_RULES_ERROR_DISMISSED });

/**
 * A write, its listing kept, and the counts re-read. The import page's selected file is re-read too:
 * its mapper and preview are another reading of the same rules, and would otherwise be stale when
 * the page is opened again.
 */
function ruleWrite(dispatch, getState, action, data) {
	const budgetId = activeBudgetId(getState);
	const dropped = { saved: false, error: null };
	dispatch({ type: IMPORT_RULES_WRITE_BEGIN });
	return Request.apiv1Post(action, data)
		.then(whileBudget(getState, budgetId, (response) => {
			dispatch({ type: IMPORT_RULES_LOADED, payload: response });
			dispatch(loadImportRuleMatches());
			const state = typeof getState === 'function' ? getState() : null;
			const batch = state && state.importing && state.importing.selectedBatch;
			if(batch) {
				dispatch(loadBatchDetail(batch));
			}
			return { saved: true, error: null, ruleId: response.rule_id === undefined ? null : response.rule_id };
		}))
		.catch(whileBudget(getState, budgetId, (error) => {
			const message = errorMessage(error);
			dispatch({ type: IMPORT_RULES_WRITE_FAIL, payload: message });
			/**
			 * A rule gone (404) or a duplicate (422) can mean the list on screen is stale: changed in
			 * another tab, or by the import page. Read it again rather than leave a ghost to click on.
			 */
			if(error && (error.status === 404 || error.status === 422)) {
				dispatch(loadImportRules());
			}
			return { saved: false, error: message };
		}))
		.then((result) => result || dropped);
}

function whileBudget(getState, budgetId, use) {
	return (answer) => (activeBudgetId(getState) === budgetId ? use(answer) : undefined);
}

function errorMessage(error) {
	if(error && error.responseJSON && error.responseJSON.error) {
		return error.responseJSON.error;
	}
	if(error && error.error) {
		return error.error;
	}
	if(error && error.message) {
		return error.message;
	}
	return 'Something went wrong saving the rule.';
}
